/**
 * Poseidon dashboard — decision-trail detail view (§3.2 B).
 *
 * Renders the complete chain for one completed trade, in order:
 *
 *   signal → feature snapshot → DeepSeek evidence/response → risk rules →
 *   human approval (if any) → MT5 order request/result → broker deals →
 *   final net P&L and post-trade tags
 *
 * A step that is genuinely absent (for example the human-approval step under
 * constrained automated execution) is labelled as absent. It is never rendered
 * as an empty box that a reader could mistake for a missing record.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { TradeDecisionTrailView } from '../api/types';
import {
  CONVICTION_LABELS,
  EXIT_REASON_LABELS,
  OUTCOME_LABELS,
  RECOMMENDATION_LABELS,
  SCHEMA_VALIDATION_LABELS,
  VERDICT_LABELS,
  toneForOutcome,
  toneForValidation,
  toneForVerdict,
} from '../lib/labels';
import { formatDateTimeWithZone, formatDuration, formatNumber, formatPercent, formatPrice, formatSigned } from '../lib/format';
import { cx } from '../lib/cx';
import type { Tone } from '../lib/staleness';
import { AiDisclaimer, AiResearchBadge, Badge, Disclosure, KeyValueList, MockNote, Unavailable } from './primitives';
import { DataTable } from './DataTable';
import type { TableColumn } from './DataTable';
import type { BrokerDealView, RiskRuleResult } from '../api/types';

function TrailStep({
  index,
  title,
  at,
  tone,
  absent = false,
  children,
}: {
  index: number;
  title: string;
  at?: string | null;
  tone: Tone;
  absent?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="trail__step">
      <div className={cx('trail__marker', `tone-${tone}`)}>{absent ? '–' : index}</div>
      <div className="trail__content">
        <div className="trail__head">
          <span className="trail__title">{title}</span>
          {at !== undefined && at !== null && <span className="trail__time">{formatDateTimeWithZone(at)}</span>}
          {absent && <Badge tone="muted">not applicable</Badge>}
        </div>
        {children}
      </div>
    </div>
  );
}

const RISK_RULE_COLUMNS: readonly TableColumn<RiskRuleResult, 'order' | 'rule' | 'verdict' | 'observed'>[] = [
  { key: 'order', header: '#', width: '34px', sortValue: (row) => row.order, render: (row) => row.order },
  { key: 'rule', header: 'Rule', sortValue: (row) => row.label, render: (row) => row.label },
  {
    key: 'verdict',
    header: 'Result',
    sortValue: (row) => String(row.passed),
    render: (row) => <Badge tone={row.passed ? 'ok' : toneForVerdict(row.verdict)}>{row.passed ? 'passed' : VERDICT_LABELS[row.verdict]}</Badge>,
  },
  {
    key: 'observed',
    header: 'Observed vs configured',
    render: (row) => (
      <span className="text-mono">
        {row.observed_value === null ? '—' : String(row.observed_value)}
        {row.unit !== null ? ` ${row.unit}` : ''} / {row.configured_limit === null ? '—' : String(row.configured_limit)}
      </span>
    ),
  },
];

const DEAL_COLUMNS: readonly TableColumn<BrokerDealView, 'ticket' | 'time' | 'entry' | 'volume' | 'price' | 'profit' | 'costs'>[] = [
  { key: 'ticket', header: 'Deal ticket', render: (row) => <span className="text-mono">{row.deal_ticket}</span> },
  { key: 'time', header: 'Executed', sortValue: (row) => row.executed_at, render: (row) => formatDateTimeWithZone(row.executed_at) },
  { key: 'entry', header: 'Entry', render: (row) => row.entry },
  { key: 'volume', header: 'Volume', align: 'right', sortValue: (row) => row.volume, render: (row) => formatNumber(row.volume, 2) },
  { key: 'price', header: 'Price', align: 'right', render: (row) => formatNumber(row.price, 5) },
  { key: 'profit', header: 'Profit', align: 'right', sortValue: (row) => row.profit, render: (row) => formatSigned(row.profit) },
  {
    key: 'costs',
    header: 'Commission / swap / fee',
    align: 'right',
    render: (row) => (
      <span className="text-mono">
        {formatNumber(row.commission, 2)} / {formatNumber(row.swap, 2)} / {formatNumber(row.fee, 2)}
      </span>
    ),
  },
];

export function DecisionTrail({ trail, mock = false }: { trail: TradeDecisionTrailView; mock?: boolean }): JSX.Element {
  const { signal, regime, feature_snapshot: features, research, risk_decision: risk, human_approval: approval, order_request: request, order_results: results, broker_deals: deals, completed_trade: trade, execution_summary: summary } = trail;

  return (
    <div className="trail">
      {/* 1 — signal */}
      <TrailStep index={1} title="Signal candidate (quantitative)" at={signal?.decision_time ?? null} tone={signal === null ? 'danger' : 'ok'} absent={signal === null}>
        {signal === null ? (
          <div className="trail__missing">No signal candidate is recorded for this decision. The trail cannot be reconstructed from the order side alone.</div>
        ) : (
          <>
            <KeyValueList
              items={[
                { key: 'Decision ID', value: <span className="text-mono">{signal.decision_id}</span> },
                { key: 'Strategy version', value: <span className="text-mono">{signal.strategy_version}</span> },
                { key: 'Symbol / side', value: `${signal.symbol} ${signal.side}` },
                { key: 'Entry type', value: signal.entry_type },
                { key: 'Proposed entry', value: formatPrice(signal.proposed_entry) },
                { key: 'Stop / target', value: `${formatPrice(signal.stop_loss)} / ${formatPrice(signal.take_profit)}` },
                { key: 'Quant score', value: formatNumber(signal.quant_score, 3) },
                { key: 'Model confidence', value: formatNumber(signal.model_confidence, 3) },
                { key: 'Features hash', value: <span className="text-mono">{signal.features_hash}</span> },
                { key: 'Lifecycle status', value: signal.status },
                {
                  key: 'Arbitration (§18.4)',
                  value:
                    signal.arbitration.outcome === 'not_applicable'
                      ? 'not applicable'
                      : `${signal.arbitration.outcome}${signal.arbitration.note === null ? '' : ` — ${signal.arbitration.note}`}`,
                },
              ]}
            />
          </>
        )}
      </TrailStep>

      {/* 2 — regime */}
      <TrailStep
        index={2}
        title="Market regime snapshot (§18.2)"
        at={regime?.bar_time ?? null}
        tone={regime === null ? 'danger' : 'ok'}
        absent={regime === null}
      >
        {regime === null ? (
          <div className="trail__missing">No regime snapshot is stored for this bar. The classifier output must be frozen with the decision (§18.6).</div>
        ) : (
          <KeyValueList
            items={[
              { key: 'Regime version', value: <span className="text-mono">{regime.regime_version}</span> },
              { key: 'Trend / volatility', value: `${regime.trend_strength} / ${regime.volatility_bucket}` },
              { key: 'Session phase', value: `${regime.session_phase} (${regime.minutes_since_open} min since open)` },
              { key: 'Inputs', value: `ADX ${regime.inputs.adx === null ? '—' : formatNumber(regime.inputs.adx, 2)} · realized-vol percentile ${regime.inputs.realized_vol_percentile === null ? '—' : formatNumber(regime.inputs.realized_vol_percentile, 3)}` },
              { key: 'Features hash', value: <span className="text-mono">{regime.features_hash}</span> },
            ]}
          />
        )}
      </TrailStep>

      {/* 3 — feature snapshot */}
      <TrailStep
        index={3}
        title="Feature snapshot (immutable)"
        at={features?.computed_at ?? null}
        tone={features === null ? 'danger' : 'ok'}
        absent={features === null}
      >
        {features === null ? (
          <div className="trail__missing">No feature snapshot is stored. Without it the decision cannot be reproduced.</div>
        ) : (
          <>
            <div className="data-clock">
              <span className="text-faint">version</span>
              <span className="text-mono">{features.feature_version}</span>
              <span className="text-faint">completed bar</span>
              <span className="text-mono">{formatDateTimeWithZone(features.completed_bar_time)}</span>
              <span className="text-faint">hash</span>
              <span className="text-mono">{features.features_hash}</span>
            </div>
            <DataTable
              rows={features.values}
              rowKey={(row) => row.name}
              caption="Feature values and their contributions"
              columns={[
                { key: 'name', header: 'Feature', sortValue: (row) => row.name, render: (row) => <span className="text-mono">{row.name}</span> },
                {
                  key: 'value',
                  header: 'Value',
                  align: 'right',
                  sortValue: (row) => (typeof row.value === 'number' ? row.value : null),
                  render: (row) => (row.value === null ? <Unavailable /> : <span className="text-mono">{String(row.value)}{row.unit === null ? '' : ` ${row.unit}`}</span>),
                },
                {
                  key: 'contribution',
                  header: 'Score contribution',
                  align: 'right',
                  sortValue: (row) => row.contribution,
                  render: (row) => (row.contribution === null ? <Unavailable /> : <span className="text-mono">{formatSigned(row.contribution, 4)}</span>),
                },
              ]}
            />
          </>
        )}
      </TrailStep>

      {/* 4 — research */}
      <TrailStep
        index={4}
        title="DeepSeek research evidence and response"
        at={research?.received_at ?? null}
        tone={research === null ? 'warn' : research.schema_validation.status === 'valid' ? 'ok' : 'danger'}
        absent={research === null}
      >
        {research === null ? (
          <div className="trail__missing">
            No research assessment is attached. That is a valid state: the order path must work without DeepSeek, and the research
            adjustment is neutral when no assessment exists (§1.4).
          </div>
        ) : (
          <div className="grid" style={{ gap: 10 }}>
            <div className="data-clock">
              <AiResearchBadge />
              <Badge tone={toneForValidation(research.schema_validation.status)}>{SCHEMA_VALIDATION_LABELS[research.schema_validation.status]}</Badge>
              {research.expired && <Badge tone="warn">expired — treated as neutral</Badge>}
              {research.conviction_tier !== null && <Badge tone="info">conviction: {CONVICTION_LABELS[research.conviction_tier]}</Badge>}
            </div>

            <KeyValueList
              items={[
                { key: 'Assessment ID', value: <span className="text-mono">{research.assessment_id}</span> },
                { key: 'Model / prompt version', value: <span className="text-mono">{research.model_id} · {research.model_version} · {research.prompt_version}</span> },
                { key: 'Coverage', value: `${research.coverage_status} · event ${research.event_direction} · relevance ${formatNumber(research.event_relevance, 3)}` },
                { key: 'Research score / confidence', value: `${formatNumber(research.research_score, 3)} / ${formatNumber(research.confidence, 3)}` },
                { key: 'Recommendation', value: RECOMMENDATION_LABELS[research.recommendation] },
                { key: 'Applied adjustment', value: `${formatSigned(research.applied_adjustment.research_adjustment, 4)} (clamped to ±${formatNumber(research.applied_adjustment.clamp_max, 2)}${research.applied_adjustment.clamped ? ', clamp engaged' : ''})` },
                { key: 'Event penalty', value: formatSigned(research.applied_adjustment.event_penalty, 4) },
                { key: 'Conviction multiplier (§19.3)', value: research.applied_adjustment.conviction_multiplier === null ? <Unavailable /> : `${formatNumber(research.applied_adjustment.conviction_multiplier, 2)}× from the fixed risk table` },
                { key: 'Call cost / latency', value: `${formatNumber(research.call.cost_usd, 5)} USD · ${formatDuration(research.call.latency_ms)} · ${research.call.prompt_tokens}+${research.call.completion_tokens} tokens · ${research.call.status} (attempt ${research.call.attempt})` },
                { key: 'Staleness deadline', value: `${formatDateTimeWithZone(research.staleness_deadline_at)}${research.expired ? ' (passed)' : ''}` },
              ]}
            />

            <div>
              <div className="ribbon__label">Source evidence</div>
              <div>
                {research.evidence_ids.map((id) => (
                  <span className="tag" key={id}>
                    {id}
                  </span>
                ))}
                {research.counter_evidence_ids.length > 0 && (
                  <>
                    <span className="text-faint" style={{ margin: '0 6px' }}>
                      counter-evidence:
                    </span>
                    {research.counter_evidence_ids.map((id) => (
                      <span className="tag" key={id} style={{ borderColor: 'var(--warn)' }}>
                        {id}
                      </span>
                    ))}
                  </>
                )}
              </div>
            </div>

            {research.risk_flags.length > 0 && (
              <div>
                <div className="ribbon__label">Risk flags</div>
                {research.risk_flags.map((flag) => (
                  <div key={`${flag.type}-${flag.severity}`} style={{ fontSize: 12.5 }}>
                    <Badge tone={flag.severity === 'high' ? 'danger' : flag.severity === 'medium' ? 'warn' : 'muted'}>{flag.severity}</Badge>{' '}
                    {flag.type.replace(/_/g, ' ')} — evidence {flag.evidence_ids.join(', ')}
                    {flag.note !== null && <> · {flag.note}</>}
                  </div>
                ))}
              </div>
            )}

            {research.reasoning_steps.length > 0 && (
              <Disclosure summary={`Bounded reasoning trace (${research.reasoning_steps.length} steps, each citing supplied evidence)`}>
                <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
                  {research.reasoning_steps.map((step) => (
                    <li key={step.claim}>
                      {step.claim} <span className="text-faint">[{step.evidence_ids.join(', ')}]</span>
                    </li>
                  ))}
                </ol>
              </Disclosure>
            )}

            <div>
              <div className="ribbon__label">Rationale (untrusted model text, quoted)</div>
              <blockquote className="disclosure" style={{ margin: 0, fontStyle: 'italic' }}>
                “{research.rationale}”
              </blockquote>
            </div>

            {research.schema_validation.errors.length > 0 && (
              <div className="dialog__error">
                <strong>Schema validation failed — the raw payload was logged, not repaired (§6.4).</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {research.schema_validation.errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
                {research.schema_validation.raw_payload_ref !== null && (
                  <div className="text-mono" style={{ fontSize: 11 }}>
                    raw payload: {research.schema_validation.raw_payload_ref}
                  </div>
                )}
              </div>
            )}

            <AiDisclaimer />
          </div>
        )}
      </TrailStep>

      {/* 5 — risk */}
      <TrailStep index={5} title="Risk engine decision" at={risk?.decided_at ?? null} tone={risk === null ? 'danger' : toneForVerdict(risk.verdict)} absent={risk === null}>
        {risk === null ? (
          <div className="trail__missing">No risk decision is stored. Every trade must have exactly one risk decision (§8.2).</div>
        ) : (
          <>
            <div className="data-clock">
              <Badge tone={toneForVerdict(risk.verdict)} large>
                {VERDICT_LABELS[risk.verdict]}
              </Badge>
              <span className="text-faint">config</span>
              <span className="text-mono">{risk.risk_config_version}</span>
              <span className="text-mono">{risk.risk_config_hash}</span>
            </div>
            {risk.sizing !== null && (
              <KeyValueList
                items={[
                  { key: 'Risk cash', value: formatNumber(risk.sizing.risk_cash, 2) },
                  { key: 'Risk per lot', value: formatNumber(risk.sizing.risk_per_lot, 2) },
                  { key: 'Raw lots → approved', value: `${formatNumber(risk.sizing.raw_lots, 3)} → ${formatNumber(risk.sizing.approved_lots, 2)} (step ${risk.sizing.volume_step})` },
                  { key: 'Strategy allocation share', value: risk.sizing.strategy_allocation_share === null ? <Unavailable /> : formatPercent(risk.sizing.strategy_allocation_share) },
                ]}
              />
            )}
            <DataTable
              rows={risk.rules}
              rowKey={(row) => row.rule_id}
              initialSort={{ key: 'order', direction: 'asc' }}
              columns={RISK_RULE_COLUMNS}
              caption="Every risk rule evaluated, including the ones that passed"
            />
            <p className="text-faint" style={{ margin: 0, fontSize: 11.5 }}>
              Rules are evaluated in the fixed order of §7.2 so a rejection reason is predictable. Passed rules are persisted too, which
              is what makes a rejection explainable in plain language.
            </p>
          </>
        )}
      </TrailStep>

      {/* 6 — human approval */}
      <TrailStep
        index={6}
        title="Human approval"
        at={approval?.decided_at ?? null}
        tone={approval === null ? 'muted' : approval.decision === 'rejected' ? 'danger' : approval.decision === 'auto_approved' ? 'warn' : 'ok'}
        absent={approval === null}
      >
        {approval === null ? (
          <div className="trail__missing">No approval record exists for this decision.</div>
        ) : approval.decision === 'auto_approved' ? (
          <div className="halt-strip halt-strip--soft" style={{ alignItems: 'flex-start' }}>
            <div>
              <strong>No human click: this order was submitted under constrained automated execution.</strong>
              <div style={{ marginTop: 4, fontSize: 12.5 }}>
                Execution mode at decision time: <span className="text-mono">{approval.execution_mode_at_decision}</span>. The risk engine
                approved it and the execution engine forwarded it directly (§19.4). The full trail above is what makes after-the-fact
                review possible instead of a pre-trade click.
              </div>
            </div>
          </div>
        ) : (
          <KeyValueList
            items={[
              { key: 'Operator', value: approval.operator ?? <Unavailable /> },
              { key: 'Decision', value: approval.decision },
              { key: 'Decided at', value: formatDateTimeWithZone(approval.decided_at) },
              { key: 'Expires at', value: formatDateTimeWithZone(approval.expires_at) },
              { key: 'Execution mode', value: <span className="text-mono">{approval.execution_mode_at_decision}</span> },
              { key: 'Reason', value: approval.reason ?? <span className="text-faint">none recorded</span> },
            ]}
          />
        )}
      </TrailStep>

      {/* 7 — MT5 order request/result */}
      <TrailStep
        index={7}
        title="MT5 order request and result"
        at={request?.submitted_at ?? null}
        tone={request === null ? 'danger' : 'ok'}
        absent={request === null}
      >
        {request === null ? (
          <div className="trail__missing">No order request is recorded for this decision.</div>
        ) : (
          <div className="grid" style={{ gap: 10 }}>
            <KeyValueList
              items={[
                { key: 'Order intent ID', value: <span className="text-mono">{request.order_intent_id}</span> },
                { key: 'Idempotency key', value: <span className="text-mono">{request.idempotency_key}</span> },
                { key: 'Request', value: `${request.request.side} ${formatNumber(request.request.lots, 2)} ${request.request.symbol} ${request.request.order_type} @ ${formatPrice(request.request.price)}` },
                { key: 'Stop / target / deviation', value: `${formatPrice(request.request.stop_loss)} / ${formatPrice(request.request.take_profit)} / ${request.request.deviation_points} pts` },
                { key: 'Magic / comment', value: <span className="text-mono">{request.request.magic_number} · {request.request.comment}</span> },
                { key: 'EA acknowledged', value: formatDateTimeWithZone(request.ea_acknowledged_at) },
                {
                  key: 'order_check',
                  value:
                    request.order_check === null ? (
                      <Unavailable reason="No OrderCheck result recorded." />
                    ) : (
                      <>
                        retcode {request.order_check.retcode ?? '—'} {request.order_check.retcode_label ?? ''} · margin{' '}
                        {formatNumber(request.order_check.margin_required, 2)} · free after{' '}
                        {formatNumber(request.order_check.free_margin_after, 2)}
                        {request.order_check.advisory_only && <span className="text-warn"> · advisory only (§16.1.7)</span>}
                      </>
                    ),
                },
              ]}
            />

            {results.map((result) => (
              <div key={result.broker_order_id} className="disclosure">
                <div className="data-clock">
                  <span className="text-faint">order ticket</span>
                  <span className="text-mono">{result.order_ticket ?? '—'}</span>
                  <Badge tone={result.final_state === 'rejected' ? 'danger' : result.final_state === 'partially_filled' ? 'warn' : 'ok'}>{result.final_state}</Badge>
                  <span className="text-faint">
                    retcode {result.retcode ?? '—'} {result.retcode_label ?? ''}
                    {result.retcode_is_retryable === true ? ' (retryable)' : result.retcode_is_retryable === false ? ' (permanent)' : ''}
                  </span>
                </div>
                <KeyValueList
                  items={[
                    { key: 'Requested / fill price', value: `${formatPrice(result.requested_price)} → ${formatPrice(result.fill_price)}` },
                    { key: 'Filled lots', value: formatNumber(result.filled_lots, 2) },
                    { key: 'Fill latency', value: formatDuration(result.fill_latency_ms) },
                    { key: 'Slippage est → actual', value: `${result.estimated_slippage_points ?? '—'} → ${result.actual_slippage_points ?? '—'} points` },
                    ...(result.rejection_reason === null ? [] : [{ key: 'Rejection reason', value: result.rejection_reason }]),
                  ]}
                />
              </div>
            ))}
          </div>
        )}
      </TrailStep>

      {/* 8 — broker deals */}
      <TrailStep index={8} title="Broker deals (source of truth for P&L)" at={deals[0]?.executed_at ?? null} tone={deals.length === 0 ? 'danger' : 'ok'} absent={deals.length === 0}>
        {deals.length === 0 ? (
          <div className="trail__missing">
            No broker deals are linked. §3.3 makes broker deal history the source of truth for closed-trade P&L, so a trade without deals
            cannot be trusted.
          </div>
        ) : (
          <DataTable rows={deals} rowKey={(row) => row.deal_ticket} columns={DEAL_COLUMNS} caption="Broker deals belonging to this position lifecycle" />
        )}
      </TrailStep>

      {/* 9 — final P&L and tags */}
      <TrailStep index={9} title="Final net P&L and post-trade tags" at={trade?.exit_timestamp ?? null} tone={trade === null ? 'danger' : toneForOutcome(trade.outcome)} absent={trade === null}>
        {trade === null ? (
          <div className="trail__missing">No completed-trade summary is linked to this decision.</div>
        ) : (
          <div className="grid" style={{ gap: 10 }}>
            <div className="data-clock">
              <Badge tone={toneForOutcome(trade.outcome)} large>
                {OUTCOME_LABELS[trade.outcome]}
              </Badge>
              <Badge tone="neutral">exit: {EXIT_REASON_LABELS[trade.exit_reason]}</Badge>
              <span className="text-faint">
                completed trade <span className="text-mono">{trade.completed_trade_id}</span>
              </span>
            </div>
            <KeyValueList
              items={[
                { key: 'Entry', value: `${formatDateTimeWithZone(trade.entry_timestamp)} · ${formatNumber(trade.entered_volume, 2)} lots @ ${formatPrice(trade.average_entry_price)}` },
                { key: 'Exit', value: `${formatDateTimeWithZone(trade.exit_timestamp)} · ${formatNumber(trade.exited_volume, 2)} lots @ ${formatPrice(trade.average_exit_price)}` },
                { key: 'Holding time', value: formatDuration(trade.holding_time_ms) },
                { key: 'Gross realized P&L', value: formatSigned(trade.gross_realized_pnl) },
                { key: 'Costs', value: `commission ${formatNumber(trade.commission, 2)} · swap ${formatNumber(trade.swap, 2)} · fees ${formatNumber(trade.fees, 2)}` },
                { key: 'Net realized P&L', value: <strong className={trade.net_realized_pnl >= 0 ? 'text-ok' : 'text-danger'}>{formatSigned(trade.net_realized_pnl)}</strong> },
                { key: 'Initial risk / R-multiple', value: `${trade.initial_risk_cash === null ? '—' : formatNumber(trade.initial_risk_cash, 2)} / ${trade.r_multiple === null ? '—' : formatNumber(trade.r_multiple, 2)}R` },
                { key: 'MFE / MAE', value: `${trade.max_favorable_excursion ?? '—'} / ${trade.max_adverse_excursion ?? '—'}` },
                { key: 'Ticketing', value: <span className="text-mono">order {trade.order_ticket ?? '—'} · position {trade.position_ticket ?? '—'} · deals {trade.deal_tickets.join(', ')}</span> },
                { key: 'Execution summary', value: `intent→fill ${formatDuration(summary.intent_to_fill_ms)} · slippage ${summary.actual_slippage_points ?? '—'} pts · costs ${summary.total_costs === null ? '—' : formatNumber(summary.total_costs, 2)}` },
              ]}
            />
            <div>
              <div className="ribbon__label">Post-trade tags</div>
              {trail.post_trade_tags.length === 0 ? (
                <span className="text-faint">No tags applied.</span>
              ) : (
                trail.post_trade_tags.map((tag) => (
                  <div key={tag.tag} style={{ fontSize: 12.5 }}>
                    <span className="tag">{tag.tag}</span> {tag.applied_by} · {formatDateTimeWithZone(tag.applied_at)}
                    {tag.note !== null && <span className="text-faint"> — {tag.note}</span>}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </TrailStep>

      <div className="text-faint" style={{ fontSize: 11.5, display: 'flex', gap: 8, alignItems: 'center' }}>
        Trail loaded at {formatDateTimeWithZone(trail.loaded_at)}. §8.2: no result is overwritten, so this view is reproducible.
        {mock && <MockNote />}
        <Link to="/trades">← back to trades</Link>
      </div>
    </div>
  );
}
